import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  ArrowRight,
  Clock3,
  House,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import sherCleanLogo from '@assets/logo_SHER_CLEAN_1788806384605.png';
import { preloadQuotePage } from '@/pages/quote-loader';

export const phone = '06.28.73.05.51';
export const phoneHref = 'tel:+33628730551';
export const whatsappHref = 'https://wa.me/33628730551';
export const email = 'contact@sherclean.fr';

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (href: string) => {
    setOpen(false);
    if (href.startsWith('#')) {
      if (window.location.pathname !== '/') setLocation(`/${href}`);
      else document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div
        className="container-wide header-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '22px 0',
        }}
      >
        <Link
          href="/"
          className="brand-mark"
          data-testid="link-home-brand"
          onClick={() => setOpen(false)}
        >
          <img src={sherCleanLogo} alt="SHER CLEAN" className="brand-logo" />
        </Link>
        <nav
          className="desktop-nav"
          aria-label="Navigation principale"
          style={{
            display: 'flex',
            gap: '29px',
            alignItems: 'center',
          }}
        >
          <a
            className="nav-link"
            href="#services"
            onClick={() => goTo('#services')}
            data-testid="link-services"
          >
            Prestations
          </a>
          <a
            className="nav-link"
            href="#methode"
            onClick={() => goTo('#methode')}
            data-testid="link-method"
          >
            Fonctionnement
          </a>
          <a
            className="nav-link"
            href="#tarif"
            onClick={() => goTo('#tarif')}
            data-testid="link-price"
          >
            Tarif
          </a>
          <Link
            href="/devis"
            className="btn-primary"
            onMouseEnter={preloadQuotePage}
            onFocus={preloadQuotePage}
            data-testid="link-header-quote"
          >
            Demander un devis <ArrowRight size={16} />
          </Link>
        </nav>
        <button
          className="mobile-menu-button"
          type="button"
          aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
          onClick={() => setOpen((value) => !value)}
          data-testid="button-mobile-menu"
          style={{
            display: 'none',
            border: 0,
            background: 'transparent',
            color: 'inherit',
            padding: 5,
          }}
        >
          {open ? <X size={23} /> : <Menu size={23} />}
        </button>
      </div>
      {open && (
        <div
          style={{
            background: 'hsl(var(--background))',
            padding: '4px 20px 24px',
            borderBottom: '1px solid hsl(var(--border))',
          }}
        >
          <nav aria-label="Menu mobile" style={{ display: 'grid', gap: 5 }}>
            <a
              className="nav-link"
              href="#services"
              onClick={() => goTo('#services')}
              data-testid="mobile-link-services"
            >
              Prestations
            </a>
            <a
              className="nav-link"
              href="#methode"
              onClick={() => goTo('#methode')}
              data-testid="mobile-link-method"
            >
              Fonctionnement
            </a>
            <a
              className="nav-link"
              href="#tarif"
              onClick={() => goTo('#tarif')}
              data-testid="mobile-link-price"
            >
              Tarif
            </a>
            <Link
              href="/devis"
              className="btn-primary"
              onClick={() => setOpen(false)}
              onMouseEnter={preloadQuotePage}
              onFocus={preloadQuotePage}
              data-testid="mobile-link-quote"
            >
              Demander un devis <ArrowRight size={16} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function MobileBar() {
  return (
    <div className="mobile-bar">
      <a href={phoneHref} data-testid="mobile-link-phone">
        <Phone size={15} /> Appeler
      </a>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noreferrer"
        data-testid="mobile-link-whatsapp"
      >
        <MessageCircle size={15} /> WhatsApp
      </a>
      <Link
        href="/devis"
        onMouseEnter={preloadQuotePage}
        onFocus={preloadQuotePage}
        data-testid="mobile-link-quote-bar"
      >
        <Send size={15} /> Devis gratuit
      </Link>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="footer">
      <div className="container-wide footer-inner">
        <div className="footer-row">
          <span>© {new Date().getFullYear()} SHER SOLUTIONS · SHER CLEAN</span>
          <span>
            Services à domicile à Troyes et alentours ·{' '}
            <a href={`mailto:${email}`}>{email}</a>
          </span>
        </div>
        <nav className="footer-legal-links" aria-label="Liens légaux">
          <Link href="/mentions-legales">Mentions légales</Link>
          <Link href="/conditions-generales-de-vente">
            Conditions générales de vente
          </Link>
          <Link href="/confidentialite">Confidentialité</Link>
        </nav>
      </div>
    </footer>
  );
}

export function TrustStrip() {
  return (
    <div className="trust-strip">
      <div className="container-wide trust-items">
        <span className="trust-item">
          <ShieldCheck size={17} /> Une relation de confiance
        </span>
        <span className="trust-item">
          <Clock3 size={17} /> Un rythme qui vous ressemble
        </span>
        <span className="trust-item">
          <MapPin size={17} /> Troyes et alentours
        </span>
        <span className="trust-item">
          <Sparkles size={17} /> Une entreprise locale, proche de vous
        </span>
      </div>
    </div>
  );
}